import { useState, useEffect, useRef } from "react";
import { Button, DatePicker, Input, Modal, Space } from "antd";
import { FilterOutlined, SearchOutlined } from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import {
  acceptRejectPendingOrderByAdminApi,
  getPendingOrdersForAdminApi,
} from "Network";
import { toastError, toastSuccess } from "helpers/toasters";
import { exportDataAsExcel } from "helpers/helpfulFunctions";

import Header from "modules/SubComponents/Header";
import SubHeader from "../SubComponents/SubHeader";
import GridTable from "modules/SubComponents/GridTable/GridTable";
import AcceptOrRejectModal from "./AcceptOrRejectModal";
import { useSelector } from "react-redux";

function PendingOrders() {
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isApprove, setIsApprove] = useState(false);
  const [rowData, setRowData] = useState({});
  const [dataSource, setDataSource] = useState([]);
  const [renderMe, setRenderMe] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(1);
  const [isExporting, setIsExporting] = useState(false);
  const [searchValues, setSearchValues] = useState({
    userName: "",
    userMobile: "",
    item: "",
    quantity: "",
    remainingQuantity: "",
    unitPrice: "",
    totalPrice: "",
    paymentType: "",
    orderType: "",
    from: "",
    to: "",
    salesPerson: "",
  });
  const [pageSize, setPageSize] = useState(10);
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const [selectedRange, setSelectedRange] = useState([]);
  const { RangePicker } = DatePicker;
  const searchInput = useRef(null);

  const { authorization } = useSelector((state) => state.auth);
  const isSalesPerson = authorization?.roles?.includes(4);
  const isFinance = authorization?.roles?.includes(2);

  const updateSearchValues = (property, newValue, otherProperty) => {
    const copyOfSearchObj = { ...searchValues };
    copyOfSearchObj[property] = newValue;
    if (otherProperty) {
      copyOfSearchObj[otherProperty.property] = otherProperty.value;
    }
    setSearchValues(copyOfSearchObj);
  };

  const handleDateChange = (dates) => {
    setSelectedRange(dates);
    if (dates) {
      const startDate = dates[0].format("YYYY-MM-DD");
      const endDate = dates[1].format("YYYY-MM-DD");
      updateSearchValues("from", startDate, { property: "to", value: endDate });
    } else {
      updateSearchValues("from", "", { property: "to", value: "" });
    }
  };

  const handleExportDataToExcel = () => {
    if (isExporting) return;
    setIsExporting(true);
    const baseProps = [
      { title: "Name", prop: "fullName" },
      { title: "Mobile", prop: "phone" },
      { title: "ID Number", prop: "idNumber" },
      { title: "Item", prop: "item" },
      { title: "Quantity", prop: "quantity" },
      { title: "Unit Price", prop: "unitPrice" },
      { title: "Total Price", prop: "price" },
      { title: "Order Type", prop: "orderType" },
      { title: "Sales Person", prop: "salesPerson" },
      { title: "Order Date", prop: "creationDate" },
      { title: "Management Fees", prop: "managementFees" },
      { title: "Payment Type", prop: "paymentType" },
    ];
    const payload = {
      isExport: true,
      currentPage,
      perPage: pageSize,
      ...searchValues,
    };
    for (let property in payload) {
      if (
        !payload[property] &&
        payload[property] !== 0 &&
        payload[property] !== false
      ) {
        delete payload[property];
      }
    }
    getPendingOrdersForAdminApi(
      payload,
      (res) => {
        if (res.success) {
          exportDataAsExcel(res.data.orders, {
            baseProps,
            tab: "Pending Orders Report",
          });
          setIsExporting(false);
        }
      },
      (res) => {
        setIsExporting(false);
        toastError(res?.data?.error);
      },
    );
  };

  const handlePageChange = (page) => {
    setLoading(true);
    setCurrentPage(page);
  };

  const handlePageSizeChange = (current, newSize) => {
    setLoading(true);
    setPageSize(newSize);
  };

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    setLoading(true);
    switch (dataIndex) {
      case "fullName":
        updateSearchValues("userName", selectedKeys[0]);
        break;
      case "phone":
        updateSearchValues("userMobile", selectedKeys[0]);
        break;
      case "item":
        updateSearchValues("item", selectedKeys[0]);
        break;
      case "quantity":
        updateSearchValues("quantity", selectedKeys[0]);
        break;
      case "remainingQuantity":
        updateSearchValues("remainingQuantity", selectedKeys[0]);
        break;
      case "unitPrice":
        updateSearchValues("unitPrice", selectedKeys[0]);
        break;
      case "price":
        updateSearchValues("totalPrice", selectedKeys[0]);
        break;
      case "paymentType":
        updateSearchValues("paymentType", selectedKeys[0]);
        break;
      case "orderType":
        updateSearchValues("orderType", selectedKeys[0]);
        break;
      case "salesPerson":
        updateSearchValues("salesPerson", selectedKeys[0]);
        break;
      default:
        break;
    }
    confirm();
  };

  const handleReset = (clearFilters, dataIndex, confirm) => {
    setLoading(true);
    switch (dataIndex) {
      case "fullName":
        updateSearchValues("userName", "");
        break;
      case "phone":
        updateSearchValues("userMobile", "");
        break;
      case "item":
        updateSearchValues("item", "");
        break;
      case "quantity":
        updateSearchValues("quantity", "");
        break;
      case "remainingQuantity":
        updateSearchValues("remainingQuantity", "");
        break;
      case "unitPrice":
        updateSearchValues("unitPrice", "");
        break;
      case "price":
        updateSearchValues("totalPrice", "");
        break;
      case "paymentType":
        updateSearchValues("paymentType", "");
        break;
      case "orderType":
        updateSearchValues("orderType", "");
        break;
      case "salesPerson":
        updateSearchValues("salesPerson", "");
        break;
      default:
        break;
    }
    clearFilters();
    confirm();
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div
        style={{
          padding: 8,
        }}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{
            marginBottom: 8,
            display: "block",
          }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{
              width: 90,
            }}
          >
            Search
          </Button>
          <Button
            onClick={() =>
              clearFilters && handleReset(clearFilters, dataIndex, confirm)
            }
            size="small"
            style={{
              width: 90,
            }}
          >
            Reset
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              confirm({
                closeDropdown: false,
              });
              setSearchText(selectedKeys[0]);
              setSearchedColumn(dataIndex);
            }}
          >
            Filter
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              close();
            }}
          >
            close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined
        style={{
          color: filtered ? "#1890ff" : undefined,
        }}
      />
    ),
    onFilter: (value, record) => true,
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{
            backgroundColor: "#ffc069",
            padding: 0,
          }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const getColumnDateProps = (dataIndex) => ({
    filterDropdown: () => (
      <div
        style={{
          padding: 8,
        }}
        className="range-picker-holder"
        onKeyDown={(e) => e.stopPropagation()}
      >
        <RangePicker
          value={selectedRange}
          allowClear
          popupClassName="all-orders-date-picker"
          inputReadOnly={false}
          onChange={handleDateChange}
          onKeyDown={(e) => e.preventDefault()}
        />
      </div>
    ),
    filterIcon: (filtered) => <FilterOutlined />,
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) => text,
  });

  const handleAcceptBtn = (e, row) => {
    setIsApprove(true);
    setRowData(row);
    setIsModalOpen(true);
  };

  const handleRejectBtn = (e, row) => {
    setIsApprove(false);
    setRowData(row);
    setIsModalOpen(true);
  };

  const handleCancel = () => {
    setRowData({});
    setIsModalOpen(false);
  };

  const handleOk = () => {
    const data = {
      orderId: rowData?.id,
      isAccepted: isApprove ? true : false,
    };
    acceptRejectPendingOrderByAdminApi(
      data,
      (success) => {
        if (success?.success) {
          toastSuccess(success.message);
          setRenderMe((prev) => !prev);
        } else {
          toastError(success.message);
        }
      },
      (fail) => {
        toastError(fail.message);
      },
    );
    setIsModalOpen(false);
  };

  const columns = [
    {
      key: "fullName",
      title: "NAME",
      dataIndex: "fullName",
      ...getColumnSearchProps("fullName"),
    },
    {
      key: "phone",
      title: "MOBILE",
      dataIndex: "phone",
      ...getColumnSearchProps("phone"),
    },
    {
      key: "idNumber",
      title: "ID NUMBER",
      dataIndex: "idNumber",
    },

    {
      key: "item",
      title: "ITEM",
      dataIndex: "item",
      ...getColumnSearchProps("item"),
    },
    {
      key: "quantity",
      title: "QUANTITY",
      dataIndex: "quantity",
      ...getColumnSearchProps("quantity"),
    },
    {
      key: "unitPrice",
      title: "UNIT PRICE",
      dataIndex: "unitPrice",
      ...getColumnSearchProps("unitPrice"),
    },
    {
      key: "price",
      title: "TOTAL PRICE",
      dataIndex: "price",
      ...getColumnSearchProps("price"),
    },
    {
      key: "orderType",
      title: "ORDER TYPE",
      dataIndex: "orderType",
      ...getColumnSearchProps("orderType"),
    },
    {
      key: "salesPerson",
      title: "SALES PERSON",
      dataIndex: "salesPerson",
      ...getColumnSearchProps("salesPerson"),
    },
    {
      key: "creationDate",
      title: "ORDER DATE",
      dataIndex: "creationDate",
      ...getColumnDateProps("creationDate"),
    },
    {
      key: "managementFees",
      title: "MANAGEMENT FEES",
      dataIndex: "managementFees",
      render: (managementFees) =>
        managementFees ? `${managementFees} EGP` : "",
    },
    {
      key: "paymentType",
      title: "PAYMENT TYPE",
      dataIndex: "paymentType",
      ...getColumnSearchProps("paymentType"),
    },
    !isSalesPerson && !isFinance
      ? {
          key: "id",
          title: "ACTIONS",
          dataIndex: "action",
          render: (_, row) => (
            <Space size={5}>
              <Button type="primary" onClick={(e) => handleAcceptBtn(e, row)}>
                Accept
              </Button>
              <Button danger onClick={(e) => handleRejectBtn(e, row)}>
                Reject
              </Button>
            </Space>
          ),
        }
      : null,
  ].filter(Boolean);

  useEffect(() => {
    const payload = {
      pageNumber: currentPage,
      perPage: pageSize,
      ...searchValues,
    };
    for (let property in payload) {
      if (!payload[property] && payload[property] !== 0) {
        delete payload[property];
      }
    }
    getPendingOrdersForAdminApi(
      payload,
      (res) => {
        if (res?.success) {
          setLoading(false);
          setDataSource(res.data.orders);
          setTotalCount(res.data.totalCount);
        } else {
          toastError(res.message);
        }
      },
      (fail) => {
        setLoading(false);
        toastError(fail.err_msg);
      },
    );
  }, [renderMe, currentPage, searchValues, pageSize]);

  return (
    <section className="users">
      <div className="users__all-contents ">
        <div className="users__content">
          <div className="users__usersData">
            <Header title="Pending Orders" />
            <SubHeader bgColor="#356ab3" title="All Pending Orders " />
            <div className="users__table grid-table mrg-ye-50 ">
              <GridTable
                loading={loading}
                columns={columns}
                dataSource={dataSource}
                rowId={"id"}
                allowExport={true}
                apiExport={true}
                isExporting={isExporting}
                onExport={handleExportDataToExcel}
                title={"Pending Orders"}
                currentPage={currentPage}
                totalCount={totalCount}
                onPageChange={handlePageChange}
                pageSize={pageSize}
                onPageSizeChange={handlePageSizeChange}
              />
            </div>
          </div>
        </div>
        {isModalOpen && (
          <Modal
            title={isApprove ? "Accept Order" : "Reject Order"}
            open={isModalOpen}
            footer={null}
            onCancel={handleCancel}
          >
            <AcceptOrRejectModal
              setRenderMe={setRenderMe}
              rowData={rowData}
              onCancel={handleCancel}
              isApprove={isApprove}
            />
          </Modal>
        )}
      </div>
    </section>
  );
}

export default PendingOrders;
